
import { Download } from "@mui/icons-material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { Button, IconButton, Paper, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import TenantContext from "../../lib/context/TenantContext";
import { type BatchHistory, useGetBatchDetail } from "../../lib/query/batch";
import { downloadFile } from "../../lib/services/blob-service";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";
import Jpg from "../../assets/jpg.svg";
import Pdf from "../../assets/pdf.svg";
import Png from "../../assets/png.svg";
import Svg from "../../assets/svg.svg";
import Txt from "../../assets/txt.svg";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const getIcon = (fileName: string) => {
  const fileExtension = fileName.split(".").pop();
  let result = Pdf;
  switch (fileExtension) {
    case "pdf":
      result = Pdf;
      break;
    case "svg":
      result = Svg;
      break;
    case "jpg":
    case "jpeg":
      result = Jpg;
      break;
    case "png":
      result = Png;
      break;
    case "txt":
      result = Txt;
      break;
    default:
      result = Pdf;
      break;
  }
  return result;
};

const BatchDetail = () => {
  const { batchId } = useParams();
  const { tenant } = useContext(TenantContext);
  const { data } = useGetBatchDetail(tenant.id, batchId ?? "");

  const onFileDownload = async (item: BatchHistory) => {
    const fileDownloadPath = `${format(item.CreatedDate, "yyyy/MM/dd")}/${item.BatchId}/input/${item.FileName}`;
    await downloadFile(fileDownloadPath);
  };

  const onExportFileDownload = async () => {
    if (data && data?.length > 0 && data[0]?.CreatedDate) {
      const fileDownloadPath = `${format(data[0]?.CreatedDate ?? "", "yyyy/MM/dd")}/${batchId}/result-${batchId}.xlsx`;
      await downloadFile(fileDownloadPath);
    }
  };
  return (
    <div className="m-4 gap-4">
      <div className="flex justify-between items-center mb-8 border p-4 bg-gray-50">
        <div className="flex flex-col gap-4">
          <Link
            to="/batchHistory"
            className="flex items-center gap-2 cursor-pointer text-blue-400"
          >
            <ArrowBackIcon />
            <span>Back To History</span>
          </Link>

          <div className="flex items-center gap-2">
            <div className="text-xl font-semibold">Batch Id: </div>
            <div className="text-gray-700 text-xl font-medium">{batchId}</div>
            <div>
              <Tooltip title="Copy to clipboard">
                <IconButton onClick={() => toast.info("Copied to clipboard")}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="text-xl font-semibold">Export Date: </div>
            <div className="text-gray-700 text-xl font-medium">
              {format(
                data?.[0]?.ExportDate ?? new Date(),
                "PPP"
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-2">
            <div className="text-xl font-semibold">Processed By: </div>
            <div className="text-gray-700 text-xl font-medium">
              {data?.[0]?.CreatedBy}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="text-xl font-semibold">Post to Tally: </div>
            <div className="flex items-center gap-1">
              {data?.[0]?.PostToTally ? (
                <>
                  <CheckCircleIcon className="text-green-600" />
                  <span className="text-green-600 font-medium">Posted</span>
                </>
              ) : (
                <>
                  <CancelIcon className="text-gray-400" />
                  <span className="text-gray-400 font-medium">Not Posted</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="text-xl font-semibold">Tally Response: </div>
            <div className="flex items-center gap-1">
              {data?.[0]?.TallyResponse ? (
                <div className={`font-medium ${
                  data[0]?.TallyResponse?.toLowerCase().includes('success') 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {data[0]?.TallyResponse}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </div>
          </div>
          <div className="flex">
            <div className="text-xl font-semibold">Download Output: </div>
            <Button onClick={() => onExportFileDownload()}>
              <CloudDownloadIcon />
            </Button>
          </div>
        </div>
      </div>

      <div className="border p-4 bg-gray-50">
        <div className="text-xl font-semibold p-6">Files Processed</div>
        <div className="flex flex-wrap gap-4 justify-between">
          {data
            ?.filter((e) => e.RowKey !== batchId)
            .map((item) => (
              <div className="items-end p-2" key={item.RowKey}>
                <Paper
                  elevation={2}
                  component="section"
                  sx={{
                    border: "1px grey",
                    height: "auto",
                    width: "auto",
                    padding: "1rem",
                    minWidth: "200px",
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex justify-center">
                      <img
                        height={48}
                        width={48}
                        className="size-12 flex-none rounded-md"
                        src={getIcon(item.FileName)}
                        alt="file icon"
                      />
                    </div>

                    <div className="flex text-sm text-gray-700 gap-2 justify-between items-center">
                      <div className="font-semibold">{item.FileName}</div>
                      <div>
                        <Button
                          variant="text"
                          onClick={() => onFileDownload(item)}
                        >
                          <Download />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Paper>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;